/**
 * Precios y medidas del packing list logístico (inventario).
 * Tabla 1 (proveedor / sin ganancia) vs tabla 2 (ganancia / BL declarado).
 */
import {
  gradoFromPackingRow,
  productConfigKey,
  round2,
  round3,
} from './invoicePtcMerchandise';

function safe(v) {
  return v == null ? '' : String(v);
}

function normPiezas(piezas) {
  return String(piezas ?? '')
    .trim()
    .replace(/\s+/g, ' ')
    .toUpperCase();
}

export function parseLogisticNum(v) {
  if (v == null || v === '') return null;
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  const n = Number(String(v).trim().replace(/\s/g, '').replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

/** 'proveedor' = tabla 1 (sin ganancia); 'ganancia' = tabla 2. */
export function logisticPricingMode(logisticPl) {
  if (!logisticPl) return 'proveedor';
  if (logisticPl.con_ganancia && !logisticPl.sin_ganancia) return 'ganancia';
  return 'proveedor';
}

export function medidasFromLogisticLinea(ln, mode) {
  if (mode === 'ganancia') {
    return {
      esp: ln.espesor_ganancia ?? ln.espesor,
      anc: ln.ancho_ganancia ?? ln.ancho,
      lar: ln.largo_ganancia ?? ln.largo,
      m3: parseLogisticNum(ln.total_m3_ganancia ?? ln.total_m3_original ?? ln.total_m3),
    };
  }
  return {
    esp: ln.espesor,
    anc: ln.ancho,
    lar: ln.largo,
    m3: parseLogisticNum(ln.total_m3),
  };
}

export function unitUsdM3FromLogistic(ln, precioIcb) {
  const icb = parseLogisticNum(precioIcb);
  if (icb != null) return icb;
  return parseLogisticNum(ln?.precio_usd_m3);
}

export function lineTotalUsdFromLogistic(ln, precioIcb, mode) {
  const { m3 } = medidasFromLogisticLinea(ln, mode);
  const usdM3 = unitUsdM3FromLogistic(ln, precioIcb);
  if (usdM3 != null && m3 != null) return round2(usdM3 * m3);
  return parseLogisticNum(ln?.total_usd);
}

function normNf(v) {
  const s = safe(v).trim();
  if (!s) return '';
  const n = parseInt(s, 10);
  return Number.isFinite(n) ? String(n) : s.toUpperCase();
}

/** Clave de cruce: NF del PL comercial (nf_celda) + grado. El logístico no usa nro_nf interno. */
export function logisticLineMatchKey(ln, commercialNf) {
  return [
    normNf(commercialNf),
    safe(ln?.grado).trim().toUpperCase(),
  ].join('|');
}

export function commercialLineMatchKey(row) {
  return [normNf(row?.nf_celda), gradoFromPackingRow(row).toUpperCase()].join('|');
}

function commercialNfFromRows(commercialRows) {
  for (const row of commercialRows || []) {
    const nf = safe(row?.nf_celda).trim();
    if (nf) return nf;
  }
  return '';
}

export function buildLogisticLineIndex(logisticPl, commercialNf = '') {
  const index = new Map();
  for (const ln of logisticPl?.lineas || []) {
    const key = logisticLineMatchKey(ln, commercialNf);
    if (!index.has(key)) index.set(key, []);
    index.get(key).push(ln);
  }
  return {
    index,
    mode: logisticPricingMode(logisticPl),
    precioIcb: logisticPl?.precio_icb,
  };
}

export function findLogisticLinesForCommercialRow(index, row) {
  const key = commercialLineMatchKey(row);
  return index.get(key) || [];
}

/** Suma M³ y USD logísticos para filas comerciales que cruzan con el PL inventario. */
export function aggregateLogisticPricingForRows(commercialRows, logisticPl) {
  const commercialNf = commercialNfFromRows(commercialRows);
  const { index, mode, precioIcb } = buildLogisticLineIndex(logisticPl, commercialNf);
  let sumM3 = 0;
  let sumUsd = 0;
  const unitSamples = new Set();

  for (const row of commercialRows || []) {
    const matches = findLogisticLinesForCommercialRow(index, row);
    for (const ln of matches) {
      const { m3 } = medidasFromLogisticLinea(ln, mode);
      const total = lineTotalUsdFromLogistic(ln, precioIcb, mode);
      const unit = unitUsdM3FromLogistic(ln, precioIcb);
      if (m3 != null && m3 > 0) sumM3 += m3;
      if (total != null && total > 0) sumUsd += total;
      if (unit != null) unitSamples.add(unit);
    }
  }

  let unitPriceUsd = null;
  if (sumM3 > 0 && sumUsd > 0) unitPriceUsd = round2(sumUsd / sumM3);
  else if (unitSamples.size === 1) unitPriceUsd = [...unitSamples][0];
  else {
    const icb = parseLogisticNum(precioIcb);
    if (icb != null) unitPriceUsd = icb;
  }

  return {
    sumM3: round3(sumM3),
    sumUsd: round2(sumUsd),
    unitPriceUsd,
    mode,
  };
}

export function groupCommercialRowsByConfig(lineas) {
  const groups = new Map();
  for (const row of lineas || []) {
    const m3 = Number(row?.total_m3);
    if (!Number.isFinite(m3) || m3 <= 0) continue;
    const key = productConfigKey(row);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(row);
  }
  return groups;
}

export function merchGroupKeyFromLinea(linea) {
  return `${normPiezas(linea?.piezasPorPaquete)}|${safe(linea.productName).trim().toUpperCase()}|${safe(linea.grado).trim().toUpperCase()}`;
}

function commercialRowsConM3(commercialLineas) {
  return (commercialLineas || []).filter((row) => {
    const m3 = Number(row?.total_m3);
    return Number.isFinite(m3) && m3 > 0;
  });
}

function tablaLogisticaLabel(mode) {
  return mode === 'ganancia' ? 'tabla 2 (con ganancia)' : 'tabla 1 (sin ganancia)';
}

/**
 * Detalle de cruce NF + grado entre PL comercial y logístico (diagnóstico PTB).
 */
export function diagnosePtbLogisticCross(commercialLineas, logisticPl) {
  const commercialRows = commercialRowsConM3(commercialLineas);
  const commercialNf = commercialNfFromRows(commercialRows);
  const { index, mode, precioIcb } = buildLogisticLineIndex(logisticPl, commercialNf);

  const commercialKeys = new Map();
  for (const row of commercialRows) {
    const key = commercialLineMatchKey(row);
    if (!commercialKeys.has(key)) {
      commercialKeys.set(key, {
        nf: safe(row?.nf_celda).trim() || '—',
        grado: gradoFromPackingRow(row) || '—',
      });
    }
  }

  const ok = [];
  const comercialSinLogistico = [];
  const comercialSinPrecio = [];

  for (const info of commercialKeys.values()) {
    const key = commercialLineMatchKey({ nf_celda: info.nf, grado_celda: info.grado });
    const matches = index.get(key) || [];
    if (!matches.length) {
      comercialSinLogistico.push(info);
      continue;
    }
    const priced = matches.filter((ln) => {
      const total = lineTotalUsdFromLogistic(ln, precioIcb, mode);
      const unit = unitUsdM3FromLogistic(ln, precioIcb);
      return (total != null && total > 0) || unit != null;
    });
    if (!priced.length) {
      comercialSinPrecio.push(info);
      continue;
    }
    const ln = priced[0];
    ok.push({
      ...info,
      unitUsdM3: unitUsdM3FromLogistic(ln, precioIcb),
      totalUsd: lineTotalUsdFromLogistic(ln, precioIcb, mode),
    });
  }

  const logisticoSinComercial = [];
  for (const ln of logisticPl?.lineas || []) {
    const key = logisticLineMatchKey(ln, commercialNf);
    if (commercialKeys.has(key)) continue;
    logisticoSinComercial.push({
      grado: safe(ln?.grado).trim() || '—',
      nroNf: ln?.nro_nf,
      nfComercial: commercialNf || '—',
    });
  }

  return {
    mode,
    commercialNf: commercialNf || '—',
    ok,
    comercialSinLogistico,
    comercialSinPrecio,
    logisticoSinComercial,
  };
}

function fmtUsd(v) {
  if (v == null || Number.isNaN(Number(v))) return '—';
  return Number(v).toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Mensaje legible para vista previa PTB cuando faltan precios logísticos. */
export function mensajePtbDiagnosticoCruce(commercialLineas, logisticPl) {
  const d = diagnosePtbLogisticCross(commercialLineas, logisticPl);
  const lines = [
    `No se pudo generar la Invoice PTB con precios del packing list logístico (${tablaLogisticaLabel(d.mode)}). Cruce por NF del comercial («${d.commercialNf}») + grado:`,
    '',
  ];

  if (d.ok.length) {
    lines.push('Cruza con precio:');
    for (const x of d.ok) {
      const precio =
        x.unitUsdM3 != null
          ? `${fmtUsd(x.unitUsdM3)} USD/m³`
          : x.totalUsd != null
            ? `${fmtUsd(x.totalUsd)} USD total`
            : 'precio cargado';
      lines.push(`• NF «${x.nf}», grado ${x.grado} → ${precio}`);
    }
    lines.push('');
  }

  if (d.comercialSinLogistico.length) {
    lines.push('Comercial sin línea logística equivalente:');
    for (const x of d.comercialSinLogistico) {
      lines.push(
        `• NF «${x.nf}», grado ${x.grado} — no hay línea en el PL logístico con ese grado (misma NF comercial).`
      );
    }
    lines.push('');
  }

  if (d.comercialSinPrecio.length) {
    lines.push('Cruza por NF + grado pero sin precio en logístico:');
    for (const x of d.comercialSinPrecio) {
      lines.push(
        `• NF «${x.nf}», grado ${x.grado} — complete USD/m³ o total USD en Inventario (${tablaLogisticaLabel(d.mode)}).`
      );
    }
    lines.push('');
  }

  if (d.logisticoSinComercial.length) {
    lines.push('Logístico sin fila comercial equivalente (no entra al PTB):');
    for (const x of d.logisticoSinComercial) {
      const nfInterna = x.nroNf != null && x.nroNf !== '' ? `, NF interna ${x.nroNf}` : '';
      lines.push(
        `• Grado ${x.grado}${nfInterna} — no hay fila comercial con NF «${x.nfComercial}» + grado ${x.grado}.`
      );
    }
    lines.push('');
  }

  if (!d.ok.length) {
    lines.push(
      'Revise que el PL logístico esté emitido, con precios cargados, y que cada grado del comercial exista en el logístico.'
    );
  }

  return lines.join('\n').trim();
}
